import {
  EXCEPTION_POLICY,
  TRANSITIONS_BY_ID,
  WORKFLOW_STATES,
  assertTransition,
  canRegisterException,
  DomainRuleError,
  type TransitionGuardContext,
} from './stateMachine'
import type {
  DomainEventType,
  Garment,
  ImmutableDomainEvent,
  JsonObject,
  LabelVersion,
  Order,
  OrderStatus,
  TransitionId,
  WorkflowState,
} from './types'

export const deepFreeze = <T>(value: T): Readonly<T> => {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value)
    for (const nested of Object.values(value as Record<string, unknown>)) deepFreeze(nested)
  }
  return value
}

export const roundMoney = (amount: number): number => Math.round((amount + Number.EPSILON) * 100) / 100

export const activeLabelOf = (garment: Garment): LabelVersion | undefined =>
  garment.labels.find((label) => label.status === 'ACTIVE')

export const activeRackBindingOf = (garment: Garment) =>
  garment.rackBindings.find((binding) => binding.active)

export const assertGarmentInvariants = (garment: Garment): void => {
  if (!garment.shortCode.trim()) throw new DomainRuleError('SHORT_CODE_REQUIRED', 'garment.shortCode is required')
  if (garment.labels.filter((label) => label.status === 'ACTIVE').length > 1) {
    throw new DomainRuleError('MULTIPLE_ACTIVE_LABELS', `Garment ${garment.id} has more than one ACTIVE label`)
  }
  if (garment.rackBindings.filter((binding) => binding.active).length > 1) {
    throw new DomainRuleError('MULTIPLE_ACTIVE_RACKS', `Garment ${garment.id} has more than one active rack binding`)
  }
}

export const deriveOrderStatus = (order: Order): OrderStatus => {
  if (order.payment.status !== 'SUCCEEDED') return 'WAITING_PAYMENT'
  const states = order.garments.map((garment) => garment.workflowState)
  if (states.includes('EXCEPTION')) return 'EXCEPTION_ATTENTION'
  if (states.length > 0 && states.every((state) => state === 'COMPLETED')) return 'COMPLETED'
  if (states.includes('PICKUP_VERIFYING')) return 'PICKUP_VERIFYING'
  if (states.length > 0 && states.every((state) => state === 'WAITING_PICKUP')) return 'READY_FOR_PICKUP'
  const fulfillmentStages = new Set(['FULFILLMENT', 'QC', 'SHELVING', 'PICKUP'])
  if (states.some((state) => fulfillmentStages.has(WORKFLOW_STATES[state].stage))) return 'IN_FULFILLMENT'
  return 'PAID'
}

export interface CreateEventInput {
  readonly id: string
  readonly type: DomainEventType
  readonly orderId: string
  readonly garmentId?: string
  readonly operatorId: string
  readonly occurredAt: string
  readonly idempotencyKey?: string
  readonly fromState?: WorkflowState
  readonly toState?: WorkflowState
  readonly metadata?: JsonObject
}

export const createImmutableEvent = (input: CreateEventInput): ImmutableDomainEvent =>
  deepFreeze({ ...input, metadata: input.metadata ?? {} }) as ImmutableDomainEvent

export const assertCommandKey = (
  transitionId: TransitionId,
  idempotencyKey: string | undefined,
  processedCommandKeys: readonly string[],
): void => {
  const transition = TRANSITIONS_BY_ID[transitionId]
  if (!transition.idempotent && !idempotencyKey?.trim()) {
    throw new DomainRuleError('IDEMPOTENCY_KEY_REQUIRED', `${transitionId} requires a unique idempotencyKey`)
  }
  if (idempotencyKey && processedCommandKeys.includes(idempotencyKey)) {
    throw new DomainRuleError('DUPLICATE_COMMAND', `Command ${idempotencyKey} was already processed`)
  }
}

export interface TransitionGarmentInput {
  readonly garment: Garment
  readonly transitionId: TransitionId
  readonly guardContext: TransitionGuardContext
  readonly operatorId: string
  readonly occurredAt: string
  readonly eventId: string
  readonly idempotencyKey?: string
  readonly metadata?: JsonObject
}

export interface TransitionGarmentResult {
  readonly garment: Garment
  readonly event?: ImmutableDomainEvent
  readonly changed: boolean
}

/**
 * Pure aggregate transition. Scan recognition is deliberately absent here;
 * callers must first reconcile reads and only invoke this on explicit confirm.
 */
export const transitionGarment = (input: TransitionGarmentInput): TransitionGarmentResult => {
  const definition = TRANSITIONS_BY_ID[input.transitionId]
  if (definition.idempotent && input.garment.workflowState === definition.to) {
    return { garment: input.garment, changed: false }
  }
  assertTransition(input.garment.workflowState, input.transitionId, input.guardContext)
  if (!definition.idempotent && !input.idempotencyKey?.trim()) {
    throw new DomainRuleError('IDEMPOTENCY_KEY_REQUIRED', `${input.transitionId} requires a unique idempotencyKey`)
  }

  let garment: Garment = { ...input.garment, workflowState: definition.to }
  if (input.transitionId === 'SEND_TO_PLANT') garment = { ...garment, sentToFactoryAt: input.occurredAt }
  if (input.transitionId === 'SEND_TO_QC') garment = { ...garment, washCompletedAt: input.occurredAt }
  if (input.transitionId === 'VERIFY_PICKUP_GARMENT') garment = { ...garment, pickupVerifiedAt: input.occurredAt }
  if (input.transitionId === 'COMPLETE_PICKUP') {
    garment = {
      ...garment,
      exception: undefined,
      rackBindings: garment.rackBindings.map((binding) => binding.active
        ? { ...binding, active: false, releasedAt: input.occurredAt }
        : binding),
    }
  }
  assertGarmentInvariants(garment)

  return {
    garment,
    changed: true,
    event: createImmutableEvent({
      id: input.eventId,
      type: definition.eventType,
      orderId: garment.orderId,
      garmentId: garment.id,
      operatorId: input.operatorId,
      occurredAt: input.occurredAt,
      idempotencyKey: input.idempotencyKey,
      fromState: input.garment.workflowState,
      toState: definition.to,
      metadata: input.metadata,
    }),
  }
}

export interface RegisterExceptionInput {
  readonly garment: Garment
  readonly reason: string
  readonly operatorId: string
  readonly occurredAt: string
  readonly eventId: string
  readonly idempotencyKey: string
}

export const registerGarmentException = (input: RegisterExceptionInput): TransitionGarmentResult => {
  if (!input.idempotencyKey.trim()) {
    throw new DomainRuleError('IDEMPOTENCY_KEY_REQUIRED', 'EXCEPTION_REGISTERED requires a unique idempotencyKey')
  }
  if (!input.reason.trim()) throw new DomainRuleError('EXCEPTION_REASON_REQUIRED', 'Exception reason is required')
  if (!canRegisterException(input.garment.workflowState)) {
    throw new DomainRuleError(
      'EXCEPTION_STAGE_BLOCKED',
      `Exceptions cannot be registered from ${WORKFLOW_STATES[input.garment.workflowState].stage}`,
      [...EXCEPTION_POLICY.allowedFromStages],
    )
  }
  const garment: Garment = {
    ...input.garment,
    workflowState: 'EXCEPTION',
    exception: {
      reason: input.reason,
      resumeState: input.garment.workflowState,
      registeredAt: input.occurredAt,
      registeredBy: input.operatorId,
    },
  }
  return {
    garment,
    changed: true,
    event: createImmutableEvent({
      id: input.eventId,
      type: EXCEPTION_POLICY.eventType,
      orderId: garment.orderId,
      garmentId: garment.id,
      operatorId: input.operatorId,
      occurredAt: input.occurredAt,
      idempotencyKey: input.idempotencyKey,
      fromState: input.garment.workflowState,
      toState: 'EXCEPTION',
      metadata: { reason: input.reason, resumeState: input.garment.workflowState },
    }),
  }
}

export interface ResolveExceptionInput {
  readonly garment: Garment
  readonly operatorId: string
  readonly occurredAt: string
  readonly eventId: string
  readonly idempotencyKey: string
  readonly resolutionNote: string
}

export const resolveGarmentException = (input: ResolveExceptionInput): TransitionGarmentResult => {
  if (input.garment.workflowState !== 'EXCEPTION' || !input.garment.exception) {
    throw new DomainRuleError('NO_ACTIVE_EXCEPTION', `Garment ${input.garment.id} has no active exception`)
  }
  if (!input.idempotencyKey.trim()) {
    throw new DomainRuleError('IDEMPOTENCY_KEY_REQUIRED', 'EXCEPTION_RESOLVED requires a unique idempotencyKey')
  }
  const resumeState = input.garment.exception.resumeState
  const garment: Garment = { ...input.garment, workflowState: resumeState, exception: undefined }
  return {
    garment,
    changed: true,
    event: createImmutableEvent({
      id: input.eventId,
      type: EXCEPTION_POLICY.resolutionEventType,
      orderId: garment.orderId,
      garmentId: garment.id,
      operatorId: input.operatorId,
      occurredAt: input.occurredAt,
      idempotencyKey: input.idempotencyKey,
      fromState: 'EXCEPTION',
      toState: resumeState,
      metadata: { resolutionNote: input.resolutionNote, resumeState },
    }),
  }
}

